import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import client, { fetcher } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUp, Play, FileText, Loader2 } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

export default function PegadaWhatIf() {
  const { t, i18n } = useTranslation();
  const { data: fleetData } = useSWR("/carbon/fleet", fetcher);
  const { data: factorsData } = useSWR("/carbon/factors", fetcher);
  const [changes, setChanges] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [baselineFrom, setBaselineFrom] = useState("2024-01-01");
  const [baselineTo, setBaselineTo] = useState("2024-12-31");

  const fleet = fleetData?.vehicles || [];
  const factors = factorsData?.factors || [];

  useEffect(() => {
    const init = {};
    for (const v of fleet) init[v.plate] = v.fuel;
    setChanges(init);
  }, [fleetData]);

  const buildFleetChanges = () =>
    fleet
      .filter((v) => changes[v.plate] && changes[v.plate] !== v.fuel)
      .map((v) => ({ plate: v.plate, new_fuel: changes[v.plate] }));

  const runSim = async () => {
    setLoading(true);
    try {
      const { data } = await client.post("/carbon/simulate", {
        baseline_period: { from: baselineFrom, to: baselineTo },
        fleet_changes: buildFleetChanges(),
      });
      setResult(data);
      toast.success(t("fase3.simulate_ok"));
    } catch (e) {
      toast.error(t("fase3.simulate_error"));
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    if (!result) return;
    setPdfLoading(true);
    try {
      const response = await client.post(
        "/carbon/simulate/export-pdf",
        {
          baseline_period: { from: baselineFrom, to: baselineTo },
          fleet_changes: buildFleetChanges(),
          lang: i18n.language,
        },
        { responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = response.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(disp);
      const now = new Date();
      const fallback = `simulacion-huella-carbono-${now
        .toISOString()
        .slice(0, 16)
        .replace(/[-:T]/g, "")}.pdf`;
      a.download = match?.[1] || fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("fase3.pdf_ok"));
    } catch (e) {
      toast.error(t("fase3.pdf_error"));
    } finally {
      setPdfLoading(false);
    }
  };

  const chartData = result
    ? [
        { label: t("fase3.baseline"), value: result.baseline_kgco2e },
        { label: t("fase3.simulated"), value: result.simulated_kgco2e },
      ]
    : [];

  return (
    <div data-testid="pegada-whatif">
      <div className="rounded-xl border border-salnes-atlantic/20 bg-salnes-atlantic/5 px-5 py-4 text-sm text-salnes-ink/90 mb-6 flex flex-col gap-1">
        <div className="font-serif text-lg text-salnes-atlantic">{t("fase3.simulate_title")}</div>
        <div>{t("fase3.simulate_intro")}</div>
        <div className="text-[11px] italic text-salnes-stone">
          {t("fase3.simulate_disclaimer")}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 rounded-2xl border hairline bg-white p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-4">
            {t("fase3.baseline_period")}
          </div>
          <div className="flex gap-2 mb-6 text-sm">
            <input
              type="date"
              value={baselineFrom}
              onChange={(e) => setBaselineFrom(e.target.value)}
              className="border hairline rounded px-3 h-9"
              data-testid="whatif-from"
            />
            <input
              type="date"
              value={baselineTo}
              onChange={(e) => setBaselineTo(e.target.value)}
              className="border hairline rounded px-3 h-9"
              data-testid="whatif-to"
            />
          </div>
          <div className="space-y-2">
            {fleet.map((v) => (
              <div
                key={v.plate}
                data-testid={`whatif-row-${v.plate}`}
                className="flex flex-wrap items-center gap-3 px-3 py-2 border-b hairline last:border-none"
              >
                <div className="font-mono text-sm text-salnes-ink w-24">{v.plate}</div>
                <div className="text-xs text-salnes-stone flex-1 min-w-[120px]">{v.type}</div>
                <span className="text-xs text-salnes-stone">→</span>
                <Select
                  value={changes[v.plate] || v.fuel}
                  onValueChange={(val) => setChanges((c) => ({ ...c, [v.plate]: val }))}
                >
                  <SelectTrigger className="w-[240px] h-9" data-testid={`whatif-select-${v.plate}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {factors.map((f) => (
                      <SelectItem key={f.code} value={f.code}>
                        {f[`name_${i18n.language}`] || f.name_gl}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button
              onClick={runSim}
              disabled={loading}
              data-testid="whatif-run"
              className="rounded-full bg-salnes-atlantic hover:bg-salnes-atlantic/90"
            >
              <Play size={13} className="mr-2" />
              {loading ? t("fase3.saving") : t("fase3.simulate_action")}
            </Button>
            {result && (
              <Button
                variant="outline"
                onClick={exportPdf}
                disabled={pdfLoading}
                data-testid="whatif-export-pdf-btn"
                className="rounded-full"
              >
                {pdfLoading ? (
                  <Loader2 size={13} className="mr-2 animate-spin" />
                ) : (
                  <FileText size={13} className="mr-2" />
                )}
                {pdfLoading ? t("fase3.pdf_loading") : t("fase3.export_pdf")}
              </Button>
            )}
          </div>
        </div>

        {result && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border hairline bg-salnes-atlantic text-white p-6 flex flex-col justify-between"
            data-testid="whatif-result"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/70">
              {t("fase3.delta")}
            </div>
            <div className="font-serif text-5xl tabular-nums">
              {result.delta_pct}%
            </div>
            <div className="text-sm text-white/80 mt-2 flex items-center gap-2">
              {result.delta_kgco2e < 0 ? (
                <ArrowDown size={14} />
              ) : (
                <ArrowUp size={14} />
              )}
              {(result.delta_kgco2e / 1000).toFixed(2)} t CO₂e
            </div>
          </motion.div>
        )}
      </div>

      {result && (
        <div className="rounded-2xl border hairline bg-white p-6 mb-6">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="value" name="kg CO₂e" fill="#0B4C7A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
